import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "menu"]

  connect() {
    this.open = false
    this.boundUpdatePosition = this.updatePosition.bind(this)
    this.boundMenuClick = this.menuClick.bind(this)
    this.menuElement = this.hasMenuTarget ? this.menuTarget : null

    if (!this.menuElement || !this.hasButtonTarget) return

    this.menuElement.addEventListener("click", this.boundMenuClick)
    this.sync()
  }

  disconnect() {
    this.removeViewportListeners()
    this.menuElement?.removeEventListener("click", this.boundMenuClick)
    this.restoreMenu()
    this.menuElement = null
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.open = !this.open
    this.sync()
  }

  hide() {
    if (!this.open) return

    this.open = false
    this.sync()
  }

  select() {
    this.hide()
  }

  beforeCache() {
    if (!this.menuElement) return

    this.open = false
    this.sync()
    this.restoreMenu()
  }

  outside(event) {
    if (!this.open) return
    if (this.element.contains(event.target)) return
    if (this.menuElement?.contains(event.target)) return

    this.hide()
  }

  sync() {
    if (!this.menuElement || !this.hasButtonTarget) return

    if (this.open) {
      this.moveMenuToBody()
      this.menuElement.classList.remove("hidden")
      this.updatePosition()
      this.addViewportListeners()
    } else {
      this.menuElement.classList.add("hidden")
      this.removeViewportListeners()
    }

    this.buttonTarget.setAttribute("aria-expanded", String(this.open))
  }

  menuClick(event) {
    if (!event.target.closest("[role='menuitem']")) return

    this.hide()
  }

  moveMenuToBody() {
    if (this.menuElement.parentElement === document.body) return

    this.placeholder = document.createComment("moonshot-dropdown-placeholder")
    this.menuElement.parentNode.insertBefore(this.placeholder, this.menuElement)
    document.body.appendChild(this.menuElement)
  }

  restoreMenu() {
    if (!this.placeholder?.parentNode || !this.menuElement) return

    this.placeholder.parentNode.insertBefore(this.menuElement, this.placeholder)
    this.placeholder.remove()
    this.placeholder = null
    this.resetPortalStyles()
  }

  resetPortalStyles() {
    this.menuElement.style.position = ""
    this.menuElement.style.top = ""
    this.menuElement.style.left = ""
    this.menuElement.style.right = ""
    this.menuElement.style.width = ""
    this.menuElement.style.maxWidth = ""
    this.menuElement.style.zIndex = ""
  }

  addViewportListeners() {
    window.addEventListener("scroll", this.boundUpdatePosition, true)
    window.addEventListener("resize", this.boundUpdatePosition)
  }

  removeViewportListeners() {
    window.removeEventListener("scroll", this.boundUpdatePosition, true)
    window.removeEventListener("resize", this.boundUpdatePosition)
  }

  updatePosition() {
    if (!this.open) return

    const gap = 8
    const viewportPadding = 8

    this.menuElement.style.right = "auto"
    this.menuElement.style.width = "max-content"
    this.menuElement.style.maxWidth = `calc(100vw - ${viewportPadding * 2}px)`

    const buttonRect = this.buttonTarget.getBoundingClientRect()
    const menuRect = this.menuElement.getBoundingClientRect()
    const alignLeft = this.menuElement.classList.contains("left-0")

    let left = alignLeft ? buttonRect.left : buttonRect.right - menuRect.width
    const minLeft = viewportPadding
    const maxLeft = Math.max(viewportPadding, window.innerWidth - menuRect.width - viewportPadding)
    left = Math.min(Math.max(left, minLeft), maxLeft)

    this.menuElement.style.position = "fixed"
    this.menuElement.style.top = `${buttonRect.bottom + gap}px`
    this.menuElement.style.left = `${left}px`
    this.menuElement.style.zIndex = "9999"
  }
}
